import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { InviteWorkerBody, StaffMember, WorkerRole } from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '@/lib/analytics/umami-client';

const STAFF_PATH = '/api/restaurants/{restaurantId}/staff';

const staffKey = (restaurantId: string) => ['staff', restaurantId] as const;

export function useStaff() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: staffKey(restaurantId),
    queryFn: async () =>
      unwrapResult<StaffMember[]>(
        await openapiClient.GET(STAFF_PATH, { params: { path: { restaurantId } } }),
      ),
    enabled: !!restaurantId,
  });
}

export function useInviteWorker() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: InviteWorkerBody) =>
      unwrapResult<StaffMember>(
        await openapiClient.POST(STAFF_PATH, { params: { path: { restaurantId } }, body }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKey(restaurantId) });
      trackEvent('setup_changed', { area: 'staff' });
    },
  });
}

export function useChangeWorkerRole() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ workerId, role }: { workerId: string; role: WorkerRole }) =>
      unwrapResult(
        await openapiClient.PATCH('/api/restaurants/{restaurantId}/staff/{workerId}/role', {
          params: { path: { restaurantId, workerId } },
          body: { role },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKey(restaurantId) });
      trackEvent('setup_changed', { area: 'staff' });
    },
  });
}

export function useSetWorkerActive() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ workerId, isActive }: { workerId: string; isActive: boolean }) =>
      unwrapResult(
        await openapiClient.POST('/api/restaurants/{restaurantId}/staff/{workerId}/active', {
          params: { path: { restaurantId, workerId } },
          body: { isActive },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKey(restaurantId) });
      trackEvent('setup_changed', { area: 'staff' });
    },
  });
}

export function useRevokeWorker() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (workerId: string) =>
      unwrapResult(
        await openapiClient.DELETE('/api/restaurants/{restaurantId}/staff/{workerId}', {
          params: { path: { restaurantId, workerId } },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKey(restaurantId) });
      trackEvent('setup_changed', { area: 'staff' });
    },
  });
}
